import { TRPCClientError } from "@trpc/client"
import type { MessagesProviderApi } from "@internal/messages"
import { trpc } from "#/lib/trpc/client"

// The transport half of the messages host contract, built once for the lifetime of the tab rather than
// per render.
//
// It lives in its own module rather than beside either of its callers, because both the Providers tab
// (src/pages/messages-shell.tsx) and the nav's unread dot need it and neither should drag the other's
// imports along — the dot in particular must not pull the panel into the nav's bundle.
//
// The Carrot half of this module went with the cloned page it served: /messages is one page again, and
// its Carrot side talks to `carrotClient` directly through the app's own hooks
// (src/components/messaging/hooks/**).

/**
 * The ONE piece of translation the provider adapter does, and the reason it exists at all.
 *
 * The messages package decides retries and terminal failures by reading a numeric `statusCode` off
 * the rejection — the shape `ResponseError` has on the carrot path (`ProviderApiError` in the
 * package). A `TRPCClientError` puts the upstream status somewhere else entirely,
 * `error.data.httpStatus`, so handing one straight through leaves `statusCode` undefined and every
 * retry switch in the package falls to its default branch. A 403 on someone else's conversation, a
 * 409 on a closed thread and a 412 for a member with no Healthie identity would all be retried four
 * times before surfacing, and the package's terminal-error handling would silently stop working.
 *
 * Deliberately NO retry here. The package owns read retries (see `useProviderThreadsQuery` and
 * `useThreadMessages`), and the send has none by design: Healthie's `createNote` takes no
 * idempotency key at the pinned API version and a note attributed to the member cannot be deleted
 * with our credential, so a retried send posts a permanent duplicate into a real clinical
 * conversation.
 */
async function withStatusCode<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (error) {
    if (error instanceof TRPCClientError) {
      const statusCode = (error.data as { httpStatus?: number } | undefined)?.httpStatus
      // The original is kept as `cause` so Sentry still reports the tRPC path, shape and message.
      throw Object.assign(new Error(error.message, { cause: error }), { statusCode })
    }
    // Anything else (an aborted fetch, a torn socket) has no upstream status to attach and is
    // already the error the caller should see.
    throw error
  }
}

// The PROVIDER (Healthie) transport, reached through the BFF over tRPC rather than the Carrot API.
//
// Handed over unconditionally by both host components (`messages-shell.tsx` and
// `ProviderUnreadMessagesDot.tsx`) — there is no flag on the transport itself. A member with no Healthie
// conversations gets an empty list, which is indistinguishable from the feature not existing; what the
// hosts DO gate on `isConsumer` is whether to ask at all, since a non-consumer has no Providers tab.
// This module just builds the adapter.
export const providerApi: MessagesProviderApi = {
  // UNPAGED, both reads: the procedures answer the member's whole inbox and a thread's whole
  // history out of the Convex mirror. `threadList` takes no input at all.
  getThreads: () => withStatusCode(() => trpc.messages.threadList.query()),
  getThreadMessages: ({ threadId }) => withStatusCode(() => trpc.messages.threadMessages.query({ threadId })),
  sendMessage: ({ threadId, body, clientMessageId }) =>
    withStatusCode(() => trpc.messages.sendThreadMessage.mutate({ threadId, body, clientMessageId })),
  // The procedure answers `{ threadId, unread }`, which the contract does not promise and no caller
  // reads. Discarded rather than widening `markRead` to return it: the package would then be able to
  // depend on a payload the next transport may not have.
  markRead: async ({ threadId }) => {
    await withStatusCode(() => trpc.messages.setThreadRead.mutate({ threadId }))
  },
  // ⚠ NOT tRPC, AND NOT A CALL AT ALL — a same-origin URL for the browser to navigate to, served by
  // a plain Hono route (packages/bff/src/routes/messageAttachment.ts). Healthie signs every
  // attachment url with a ten-second expiry, so the BFF mints one per request and 302s to it; a
  // procedure would answer JSON and spend those seconds on a client round trip, and would put a
  // bearer credential for a patient's file into this tab's memory.
  //
  // `/bff` is same-origin and relative, exactly as the tRPC client's own `url` is
  // (packages/bff-client/src/client.ts), so the session cookie rides along on the navigation and no
  // runtime config is needed. `kind` is a closed lowercase vocabulary the route validates, so it is
  // interpolated as-is; `messageId` comes from a payload rather than a member, and is encoded
  // anyway — a path segment is the one place an unexpected `/` would silently change the request.
  attachmentUrl: ({ messageId, kind }) => `/bff/messages/attachment/${encodeURIComponent(messageId)}/${kind}`
}
